import { SBP_MIN_AMOUNT_RUBLES } from 'modules/organizer/domain/constants/wallet-topup';
import { TournamentPlan } from 'modules/organizer/domain/interfaces/tournament-plan';
import { formatRublesFromMinorUnits } from 'modules/organizer/domain/utils/money';
import { formatSubscriptionPeriod } from 'modules/organizer/domain/utils/subscription-period';
import {
  walletTopupFormValidator,
  WalletTopupFormValues,
} from 'modules/organizer/domain/validators/wallet-topup.validator';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import UDFormInput from 'ud-form/components/input';
import UDButton from 'ud-ui/components/button';
import UDCard from 'ud-ui/components/card';
import UDText from 'ud-ui/components/text';
import { theme } from 'styles/theme/theme';

interface SubscriptionPlanPickerProps {
  plans: TournamentPlan[];
  selectedPlanId: number;
  onSelect: (planId: number) => void;
  locked: boolean;
}

export const SubscriptionPlanPicker: React.FC<SubscriptionPlanPickerProps> = ({ plans, selectedPlanId, onSelect, locked }) => {
  if (plans.length === 0) {
    return (
      <UDText type="body-text" color={theme.colors.Gray}>
        Тарифы пока недоступны.
      </UDText>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {plans.map((plan) => {
        const selected = plan.id === selectedPlanId;

        return (
          <UDCard
            key={plan.id}
            className={selected ? 'border-darkBlue-400' : undefined}
            onClick={locked ? undefined : () => onSelect(plan.id)}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <UDText type="subhead">{plan.name}</UDText>
                <UDText type="footnote" color={theme.colors.Gray}>
                  {formatRublesFromMinorUnits(plan.priceInMinorUnits)} ₽ / {formatSubscriptionPeriod(plan.period)}
                </UDText>
              </div>
              {selected && (
                <UDText type="footnote" color={theme.colors.Gray}>
                  Выбран
                </UDText>
              )}
            </div>
          </UDCard>
        );
      })}
    </div>
  );
};

interface SubscriptionTopupFormProps {
  initialAmount: number;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (values: WalletTopupFormValues) => Promise<void>;
}

export const SubscriptionTopupForm: React.FC<SubscriptionTopupFormProps> = ({
  initialAmount,
  isSubmitting,
  error,
  onSubmit,
}) => {
  const handleSubmit = async (values: WalletTopupFormValues, helpers: FormikHelpers<WalletTopupFormValues>) => {
    try {
      await onSubmit(values);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik<WalletTopupFormValues>
      initialValues={{ amount: initialAmount }}
      enableReinitialize
      validationSchema={walletTopupFormValidator}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting: formSubmitting }) => (
        <Form className="flex flex-col gap-4">
          <UDText type="headline">Пополнить баланс</UDText>
          <UDText type="body-text" color={theme.colors.Gray}>
            Оплата по СБП. После зачисления деньги остаются на кошельке — тариф подключается отдельно.
          </UDText>
          <UDFormInput name="amount" type="number" label="Сумма, ₽" min={SBP_MIN_AMOUNT_RUBLES} step="1" />
          {error && (
            <UDText type="footnote" color={theme.colors.CaseErrorText}>
              {error}
            </UDText>
          )}
          <UDButton type="submit" variant="primary" loading={isSubmitting || formSubmitting}>
            Пополнить по СБП
          </UDButton>
        </Form>
      )}
    </Formik>
  );
};
